import { WeightUnit } from '@/enums'
import { GeoLabel } from '@/types'
import { nextTick } from 'vue'

export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const convertToRecord = (array: any[]): Record<number, string> => {
  return array.reduce<Record<number, string>>((acc, item) => {
    acc[item.id] = item.name
    return acc
  }, {})
}

export const getGeoLabel = (countryId: number): GeoLabel => {
  // For USA only use another labels
  return new GeoLabel({
    cityLabel: countryId == 7 ? 'MetabolicLang.addClient.city' : 'MetabolicLang.addClient.city2',
    zipCodeLabel: countryId == 7 ? 'MetabolicLang.addClient.zipCode' : 'MetabolicLang.addClient.zipCode2',
  })
}

export const calculateBodyMassIndexText = (value: number): string => {
  /*
public decimal BMI => Math.Round((Height > 0) ?
(decimal)(Weight / (Height * Height)) * (MeasurementType == MeasurementType.Metric ? 10000 : 703)
: 0);
 */

  if (value < 18) {
    return 'Underweight'
  } else if (value > 25 && value <= 30) {
    return 'Overweight'
  } else if (value > 30) {
    return 'Obesity'
  }
  return 'HealthyWeight'
}

export const calculateBodyMassIndexColor = (value: number): string => {
  if (value < 18) {
    return 'danger'
  } else if (value > 25 && value <= 30) {
    return 'warning'
  } else if (value > 30) {
    return 'danger'
  }
  return 'success'
}

export const calculateBodyMassIndex = (
  height: number | undefined | null,
  weight: number | undefined | null,
  weightUnit: WeightUnit,
): number => {
  if (weightUnit == WeightUnit.kg) {
    // BMI Formula: weight (kg) / height (m)^2
    return height && weight ? Math.round(weight / (height / 100) ** 2) : 0
  } else {
    // BMI = (weight in lb / height in in²) × 703
    return height && weight ? Math.round((weight / height ** 2) * 703) : 0
  }
}

export const compareObjects = <T extends Record<string, unknown>>(object1: T, object2: T): boolean => {
  return Object.keys(object1).some((key) => {
    const value1 = object1[key as keyof T]
    const value2 = object2?.[key as keyof T]

    if (Array.isArray(value1) && Array.isArray(value2)) {
      // Check arrays for equality
      return !compareArrays(value1, value2)
    } else if (isObject(value1) && isObject(value2)) {
      // Check nested objects
      return compareObjects(value1 as Record<string, unknown>, value2 as Record<string, unknown>)
    } else {
      // Primitive comparison
      return value1 !== value2
    }
  })
}

export const splitArrayIntoArrays = <T>(arr: T[], numOfArrays: number): T[][] => {
  const result: T[][] = []
  const chunkSize = Math.ceil(arr.length / numOfArrays)
  for (let i = 0; i < numOfArrays; i++) {
    result.push(arr.slice(i * chunkSize, (i + 1) * chunkSize))
  }
  return result
}

// Helper to compare arrays
export const compareArrays = (arr1: unknown[], arr2: unknown[]): boolean => {
  if (arr1.length !== arr2.length) return false
  return arr1.every((item, index) => {
    const other = arr2[index]
    if (Array.isArray(item) && Array.isArray(other)) {
      return compareArrays(item, other) // Recursively compare arrays
    } else if (isObject(item) && isObject(other)) {
      return compareObjects(item as Record<string, unknown>, other as Record<string, unknown>)
    } else {
      return item === other // Primitive comparison
    }
  })
}

// Helper to check if a value is an object
const isObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

export const scrollToError = async () => {
  const invalidField = document.querySelector('.va-message-list')
  if (invalidField) {
    await nextTick()
    invalidField.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

export const enforcePlus = (event: string | InputEvent): string => {
  const inputValue = typeof event === 'string' ? event : ((event.target as HTMLInputElement)?.value ?? '+')

  if (!inputValue) return '+'
  if (!inputValue.startsWith('+')) {
    return '+' + inputValue.replace(/^\+/, '')
  }
  return inputValue
}

export const enforceMinus = (event: string | InputEvent): string => {
  const inputValue = typeof event === 'string' ? event : ((event.target as HTMLInputElement)?.value ?? '+')

  if (!inputValue) return '-'
  if (!inputValue.startsWith('-')) {
    return '-' + inputValue.replace(/^-/, '')
  }
  return inputValue
}

export const enforcePlusOrEmpty = (event: string | InputEvent): string => {
  const inputValue = typeof event === 'string' ? event : ((event.target as HTMLInputElement)?.value ?? '')

  if (inputValue === '+' || inputValue === '') return ''
  if (!inputValue.startsWith('+')) {
    return '+' + inputValue.replace(/^\+/, '')
  }
  return inputValue
}

export const removeByIndex = <T>(array: T[], index: number): T[] => {
  if (index >= 0 && index < array.length) {
    array.splice(index, 1)
  }
  return array
}

//new compare functions
export const normalizeValue = (val: unknown): unknown => {
  if (typeof val === 'string') {
    return val.trim()
  }
  if (typeof val === 'number') {
    return Number(val)
  }
  if (typeof val === 'boolean') {
    return val
  }
  return val
}

export const isEqualsArrays = (arr1: unknown[], arr2: unknown[]): boolean => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false
  }

  if (arr1.length !== arr2.length) {
    return false
  }

  return arr1.every((item, index) => {
    const other = arr2[index]

    if (Array.isArray(item) && Array.isArray(other)) {
      return isEqualsArrays(item, other)
    } else if (isObject(item) && isObject(other)) {
      return isEqualsObjects(item, other)
    } else {
      const normalizedItem = normalizeValue(item)
      const normalizedOther = normalizeValue(other)
      return normalizedItem === normalizedOther
    }
  })
}

export const isEqualsObjects = <T extends Record<string, unknown>>(obj1: T, obj2: T): boolean => {
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  return keys1.every((key) => {
    const val1 = obj1[key]
    const val2 = obj2[key]

    if (Array.isArray(val1) && Array.isArray(val2)) {
      return isEqualsArrays(val1, val2)
    } else if (isObject(val1) && isObject(val2)) {
      return isEqualsObjects(val1 as Record<string, unknown>, val2 as Record<string, unknown>)
    } else {
      const normalized1 = normalizeValue(val1)
      const normalized2 = normalizeValue(val2)
      return normalized1 === normalized2
    }
  })
}

export const checkOverflow = (el: EventTarget | null): boolean => {
  if (el && el instanceof HTMLElement) {
    return el.scrollWidth > el.clientWidth
  }
  return false
}

export const replaceMacros = (template: string, values: any): string => {
  return template.replace(/\[([^\]]+)\]/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  })
}
